import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { Edit as EditIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { useUser, authClient } from '../../lib/auth-client';
import { userApi, type UserInfo } from '../../services/userApi';

export default function ProfilePage() {
	const { user } = useUser();
	const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
	const [isEditingName, setIsEditingName] = useState(false);
	const [isChangingPassword, setIsChangingPassword] = useState(false);
	const [name, setName] = useState('');
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [successMessage, setSuccessMessage] = useState('');
	const [errorMessage, setErrorMessage] = useState('');

	useEffect(() => {
		if (user?.name) {
			setName(user.name);
		}
		// Fetch user info with password status
		const fetchUserInfo = async () => {
			try {
				const info = await userApi.getCurrentUser();
				setUserInfo(info);
			} catch (error) {
				console.error('Failed to fetch user info:', error);
			}
		};
		fetchUserInfo();
	}, [user]);

	const handleUpdateName = async () => {
		try {
			const { error } = await authClient.updateUser({
				name
			});

			if (error) {
				setErrorMessage(error.message || 'Cập nhật thất bại');
			} else {
				setSuccessMessage('Cập nhật họ tên thành công!');
				setIsEditingName(false);
				window.location.reload();
			}
		} catch (_error) {
			setErrorMessage('Đã xảy ra lỗi khi cập nhật họ tên');
		}
	};

	const handleResetPassword = async () => {
		try {
			const { error } = await authClient.changePassword({
				currentPassword,
				newPassword,
				revokeOtherSessions: false
			});

			if (error) {
				setErrorMessage(error.message || 'Đổi mật khẩu thất bại');
			} else {
				setSuccessMessage('Đổi mật khẩu thành công!');
				setIsChangingPassword(false);
				setCurrentPassword('');
				setNewPassword('');
			}
		} catch (_error) {
			setErrorMessage('Đã xảy ra lỗi khi đổi mật khẩu');
		}
	};

	return (
		<Box sx={{
			width: '100%',
			maxWidth: '600px',
			px: { xs: 2, sm: 0 },
			py: { xs: 2, sm: 3 }
		}}>
			<Typography
				component="h1"
				variant="h4"
				sx={{
					mb: 3,
					fontWeight: 'bold',
					fontSize: { xs: '1.75rem', sm: '2.125rem' }
				}}
			>
				Thông tin cá nhân
			</Typography>

			{successMessage && (
				<Alert
					severity="success"
					onClose={() => setSuccessMessage('')}
					sx={{ mb: 2 }}
				>
					{successMessage}
				</Alert>
			)}

			{errorMessage && (
				<Alert
					severity="error"
					onClose={() => setErrorMessage('')}
					sx={{ mb: 2 }}
				>
					{errorMessage}
				</Alert>
			)}

			<Card sx={{ mb: 2 }}>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Thông tin người dùng
					</Typography>

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
						<Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<Typography variant="body2" color="text.secondary">
									Họ tên:
								</Typography>
								{!isEditingName && (
									<Button
										size="small"
										startIcon={<EditIcon />}
										onClick={() => setIsEditingName(true)}
									>
										Chỉnh sửa
									</Button>
								)}
							</Box>
							{isEditingName ? (
								<Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
									<TextField
										fullWidth
										size="small"
										value={name}
										onChange={(e) => setName(e.target.value)}
									/>
									<Button
										variant="contained"
										size="small"
										onClick={handleUpdateName}
									>
										Lưu
									</Button>
									<Button
										variant="outlined"
										size="small"
										onClick={() => {
											setIsEditingName(false);
											setName(user?.name || '');
										}}
									>
										Hủy
									</Button>
								</Box>
							) : (
								<Typography variant="body1">
									{user?.name || 'N/A'}
								</Typography>
							)}
						</Box>

						<Box>
							<Typography variant="body2" color="text.secondary">
								Email:
							</Typography>
							<Typography variant="body1">
								{user?.email || 'N/A'}
							</Typography>
						</Box>

						<Box>
							<Typography variant="body2" color="text.secondary">
								Vai trò:
							</Typography>
							<Typography variant="body1">
								{user?.role === 'admin'
									? 'Quản trị viên'
									: user?.role === 'manager'
										? 'Quản lý'
										: 'Người dùng'}
							</Typography>
						</Box>

						{user?.MaCB && (
							<Box>
								<Typography variant="body2" color="text.secondary">
									Mã cán bộ:
								</Typography>
								<Typography variant="body1">
									{user.MaCB}
								</Typography>
							</Box>
						)}

						{user?.maKhoa && (
							<Box>
								<Typography variant="body2" color="text.secondary">
									Mã khoa:
								</Typography>
								<Typography variant="body1">
									{user.maKhoa}
								</Typography>
							</Box>
						)}
					</Box>
				</CardContent>
			</Card>

			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						{userInfo?.hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu mới'}
					</Typography>

					{!isChangingPassword ? (
						<Button
							variant="outlined"
							startIcon={<VpnKeyIcon />}
							onClick={() => setIsChangingPassword(true)}
						>
							{userInfo?.hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu mới'}
						</Button>
					) : (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{userInfo?.hasPassword && (
								<TextField
									label="Mật khẩu hiện tại"
									type="password"
									fullWidth
									size="small"
									value={currentPassword}
									onChange={(e) => setCurrentPassword(e.target.value)}
								/>
							)}
							<TextField
								label="Mật khẩu mới"
								type="password"
								fullWidth
								size="small"
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								helperText="Mật khẩu phải có ít nhất 6 ký tự"
							/>
							<Box sx={{
								display: 'flex',
								flexDirection: { xs: 'column', sm: 'row' },
								gap: 1
							}}>
								<Button
									variant="contained"
									onClick={handleResetPassword}
								>
									{userInfo?.hasPassword ? 'Đổi mật khẩu' : 'Đặt mật khẩu'}
								</Button>
								<Button
									variant="outlined"
									onClick={() => {
										setIsChangingPassword(false);
										setCurrentPassword('');
										setNewPassword('');
									}}
								>
									Hủy
								</Button>
							</Box>
						</Box>
					)}
				</CardContent>
			</Card>
		</Box>
	);
}